// src/components/DateField.tsx
//
// Dual-calendar date input — a plain <input type="date"> with the HEBREW
// date rendered underneath in muted small text (e.g. "13 Tamuz 5786").
// Drop-in replacement for the bare date inputs on the analytics/financials
// filter bars; the caption is absolutely positioned inside a fixed-height
// reserve so it never reflows the filter row (mobile included).
//
// Hebrew conversion uses @hebcal/core's HDate (greg → hebrew), formatted as
// "{day} {MonthName} {year}" to match the app's other Hebrew renderings
// (HebrewDatePicker labels like "13 Tammuz 5786"). Value strings are parsed
// as LOCAL dates (new Date(y, m-1, d)) — never `new Date('yyyy-MM-dd')`,
// which would be UTC and shift the Hebrew day near midnight.

import { HDate } from '@hebcal/core';

/** "yyyy-MM-dd" → "13 Tamuz 5786" (empty/invalid → ''). */
export function hebrewDateCaption(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return '';
  const [, y, mo, d] = m;
  try {
    const hd = new HDate(new Date(Number(y), Number(mo) - 1, Number(d)));
    return `${hd.getDate()} ${hd.getMonthName()} ${hd.getFullYear()}`;
  } catch {
    return '';
  }
}

/** "yyyy-MM" → Hebrew date of the 1st (for month-only inputs). */
export function hebrewMonthCaption(value: string): string {
  return /^\d{4}-\d{2}$/.test(value) ? hebrewDateCaption(`${value}-01`) : '';
}

interface DateFieldProps {
  value: string;                     // 'yyyy-MM-dd' or ''
  onChange: (v: string) => void;
  label?: string;                    // optional caption above the input (matches filter-bar label style)
  min?: string;
  max?: string;
  className?: string;                // extra classes for the <input> itself (e.g. width overrides)
}

export default function DateField({ value, onChange, label, min, max, className = '' }: DateFieldProps) {
  const hebrew = hebrewDateCaption(value);

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      )}
      {/* The caption is absolutely positioned below the input so DateField
          occupies EXACTLY the same box as the bare <input type="date"> it
          replaces — the filter bars align controls with items-end, and a
          reserved caption slot would push the input out of line. The caption
          renders into the row gap / container padding underneath. */}
      <div className="relative">
        <input
          type="date"
          value={value}
          min={min}
          max={max}
          onChange={(e) => onChange(e.target.value)}
          className={`px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 [color-scheme:dark] ${className}`}
        />
        {hebrew && (
          <span className="absolute left-0.5 top-full mt-px text-[10px] text-slate-500 leading-3 whitespace-nowrap pointer-events-none">
            {hebrew}
          </span>
        )}
      </div>
    </div>
  );
}
